import { cx } from "@suse-o11y/class-variance";
import { IconMuted, IconQuestionCircleOutlined } from "@suse-o11y/icons";
import { isNonEmptyArray } from "@suse-o11y/non-empty-array";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React, { type FC } from "react";

import { Permission } from "~/models/dto";

import { healthStateToColorToken } from "../../../../common/functions";
import type { HealthStateValue, SyncedSilencing } from "../../../../common/openapi";
import { usePermissions } from "../../../../common/use-permissions";
import { Widget, WidgetBody, WidgetHeader } from "../../../../components/_widget";
import { HealthStateBadge } from "../../../../components/badges/HealthStateBadge";
import { healthStateToBackgroundColor } from "../../../../components/badges/health-state-background-color";
import { MutedSinceMessage } from "../../../../components/muted-since-message";
import { Markdown } from "../../../../components/react/markdown/Markdown";
import { FormatViewTime } from "../../../../components/react/viewtime/FormatViewTime";

export interface MonitorStatusDrawerHealthStateProps {
	health: HealthStateValue;
	selectedTimestamp: number;
	triggeredTimeStamp: number;
	silencing: ReadonlyArray<SyncedSilencing>;
	reason?: string;
}

export const MonitorStatusDrawerHealthState: FC<MonitorStatusDrawerHealthStateProps> = ({
	health,
	selectedTimestamp,
	triggeredTimeStamp,
	silencing,
	reason,
}) => {
	const permissions = usePermissions();

	return (
		<Widget frameless>
			<WidgetHeader
				size="xl"
				title={
					<>
						Monitor status for <FormatViewTime time={selectedTimestamp} />
					</>
				}
			/>

			<WidgetBody card className="flex flex-row overflow-hidden text-grey-800">
				<StatusCard
					className="w-1/4 border-0 border-l-[3px] border-solid"
					style={{
						backgroundColor: healthStateToBackgroundColor(health),
						borderColor: `var(${healthStateToColorToken(health)})`,
					}}
					title="Health state"
					tooltip="This is the moment when the monitor triggered a new state based on the processed data."
				>
					<div className="inline-flex gap-2">
						<HealthStateBadge state={health} />
						{permissions?.hasSystemPermission(Permission.GetSilencingRules) && isNonEmptyArray(silencing) && (
							<TooltipBoundary>
								<TooltipTrigger asChild={false} className="flag-badge flag-badge-alt uppercase" type="button">
									<IconMuted className="flag-badge-icon" /> muted
								</TooltipTrigger>

								<Tooltip className="tooltip/tooltip" side="right">
									<MutedSinceMessage reasons={silencing} />
								</Tooltip>
							</TooltipBoundary>
						)}
					</div>
				</StatusCard>

				<StatusCard className="w-1/4 border-0 border-grey-300 border-r border-l border-solid" title="Triggered time">
					<FormatViewTime time={triggeredTimeStamp} />
				</StatusCard>

				<StatusCard className="w-2/4" title="Reason">
					{reason != null && reason !== "" ? (
						<Markdown text={reason} />
					) : (
						<span className="text-grey-600">No reason provided by the monitor</span>
					)}
				</StatusCard>
			</WidgetBody>
		</Widget>
	);
};

const StatusCard: FC<
	React.PropsWithChildren<{
		title: string;
		tooltip?: string;
		style?: React.CSSProperties;
		className?: string;
	}>
> = ({ className, title, children, tooltip, ...rest }) => (
	<div className={cx("bg-white p-4", className)} {...rest}>
		<p className="mb-1 font-medium text-sm">
			{title}
			{tooltip && (
				<TooltipBoundary>
					<TooltipTrigger aria-label="More information" className="button button-sm button-square">
						<IconQuestionCircleOutlined className="text-core-icon-neutral-tertiary" />
					</TooltipTrigger>

					<Tooltip className="tooltip/tooltip">{tooltip}</Tooltip>
				</TooltipBoundary>
			)}
		</p>
		{children}
	</div>
);
